import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle, Loader, Phone, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useUserStore } from '../store/userStore';
import { ordersApi } from '../services/ordersApi';
import { pickTerminalGroupId } from '../utils/terminalGroupSync';
import { terminalGroupsQueryOptions } from '../queries/terminalGroups';
import { operatingStatusQueryOptions } from '../queries/operatingStatus';
import { loyaltyPolicyQueryOptions } from '../queries/loyaltyPolicy';
import { myOrdersQueryKey } from '../queries/myOrders';
import { STORAGE_KEY as PENDING_PAYMENT_KEY } from './PaymentReturnScreen';
import { appPath } from '../routes/appPaths';
import { applyGuestDiscount } from '../loyalty/guestDiscount';

export default function CheckoutScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    items,
    total,
    clear,
    selectedTerminalGroupId,
    setSelectedTerminalGroupId,
    deliveryMode,
    deliveryAddress,
    deliveryTerminalGroupId,
  } = useCart();
  const storedPhone = useUserStore(s => s.phone);
  const token = useUserStore(s => s.token);
  const profileFirstName = useUserStore(s => s.firstName);
  const profileLastName = useUserStore(s => s.lastName);

  const [phone, setPhone] = useState(storedPhone ?? '');
  const [comment, setComment] = useState('');
  const [payMode, setPayMode] = useState<'online' | 'cash'>('online');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: terminalGroups = [], isPending, refetch } = useQuery(
    terminalGroupsQueryOptions(),
  );
  const { data: operatingStatus } = useQuery(operatingStatusQueryOptions());
  const { data: loyaltyPolicy } = useQuery(loyaltyPolicyQueryOptions());
  const scheduleClosed = operatingStatus && !operatingStatus.isOpen;
  const pointsLoading = isPending && terminalGroups.length === 0;
  const pricing = applyGuestDiscount(total, loyaltyPolicy?.discountPercent ?? 0);

  useEffect(() => {
    if (terminalGroups.length === 0) return;
    setSelectedTerminalGroupId(prev => pickTerminalGroupId(terminalGroups, prev));
  }, [terminalGroups, setSelectedTerminalGroupId]);

  async function handleSubmit() {
    if (deliveryMode === 'pickup' || deliveryMode === 'delivery') return;
    if (!token) {
      setError('Войдите в аккаунт');
      return;
    }
    if (scheduleClosed) {
      setError('Кафе сейчас закрыто');
      return;
    }
    const accountPhone = storedPhone?.trim();
    if (!accountPhone) {
      setError('Укажите номер телефона в профиле');
      return;
    }
    if (deliveryMode === 'delivery' && !deliveryAddress) {
      setError('Укажите адрес доставки');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fresh = await refetch();
      if (fresh.error) {
        setError('Не удалось проверить точки самовывоза');
        return;
      }
      const groups = fresh.data ?? [];
      const opFresh = await queryClient.fetchQuery(operatingStatusQueryOptions());
      if (opFresh && !opFresh.isOpen) {
        setError('Кафе сейчас закрыто');
        return;
      }

      let terminalGroupId: string;
      let serviceType: 'DeliveryPickUp' | 'DeliveryByCourier';

      if (deliveryMode === 'delivery' && deliveryAddress) {
        serviceType = 'DeliveryByCourier';
        terminalGroupId = deliveryTerminalGroupId || pickTerminalGroupId(groups, selectedTerminalGroupId);
      } else {
        serviceType = 'DeliveryPickUp';
        terminalGroupId = pickTerminalGroupId(groups, selectedTerminalGroupId);
      }

      if (!terminalGroupId) {
        setError('Не выбрана точка самовывоза');
        return;
      }
      const returnUrl = `${window.location.origin}${appPath('/payment-return')}`;
      const failUrl = `${window.location.origin}${appPath('/payment-failed')}`;
      const fn = profileFirstName.trim();
      const ln = profileLastName.trim();
      const result = await ordersApi.createOrder(token, {
        phone: accountPhone,
        serviceType,
        terminalGroupId,
        ...(fn && fn !== 'Гость' ? { firstName: fn } : {}),
        ...(ln ? { lastName: ln } : {}),
        comment: comment.trim() || undefined,
        items: items.map(i => ({
          productId: i.id,
          amount: i.quantity,
          price: i.price,
        })),
        ...(deliveryMode === 'delivery' && deliveryAddress ? { address: deliveryAddress } : {}),
        paymentMethod: payMode,
        ...(payMode === 'online'
          ? { returnUrl, failUrl, paymentConfirmation: 'embedded' as const }
          : {}),
      });

      await queryClient.invalidateQueries({ queryKey: myOrdersQueryKey(token) });

      if (payMode === 'online') {
        if (result.confirmationToken) {
          sessionStorage.setItem(PENDING_PAYMENT_KEY, result.orderId);
          navigate(appPath('/payment-widget'), {
            state: {
              confirmationToken: result.confirmationToken,
              orderId: result.orderId,
              returnUrl,
            },
          });
          return;
        }
        if (result.paymentUrl) {
          sessionStorage.setItem(PENDING_PAYMENT_KEY, result.orderId);
          window.location.assign(result.paymentUrl);
          return;
        }
        setError('Не удалось начать оплату');
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        clear();
        navigate(appPath('/home'), { replace: true });
      }, 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при создании заказа');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#0a0a0a' }}>
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          className="text-center px-8"
        >
          <CheckCircle size={72} className="mx-auto mb-4" style={{ color: '#fff' }} strokeWidth={1.5} />
          <h2 className="text-2xl font-bold text-white mb-2">Заказ принят!</h2>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Подойдите на кассу для получения заказа
          </p>
        </motion.div>
      </div>
    );
  }

  const cardStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
  } as const;
  const labelStyle = { color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' } as const;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a0a0a', color: '#fff' }}>
      <div className="max-w-md mx-auto w-full flex flex-col flex-1">

        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-5 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-90"
            style={cardStyle}
          >
            <ArrowLeft size={20} style={{ color: '#fff' }} />
          </button>
          <h1
            className="text-xl font-bold uppercase"
            style={{ letterSpacing: '-0.03em', fontFamily: 'Unbounded, sans-serif' }}
          >
            Оформление
          </h1>
        </div>

        <div className="flex-1 px-4 pb-32 space-y-3">

          {/* Service type */}
          <div className="rounded-3xl p-4" style={cardStyle}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={labelStyle}>
              Тип заказа
            </p>
            <div className="flex p-1 gap-1 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div
                className="flex-1 py-2.5 text-sm font-semibold text-center rounded-xl transition-all"
                style={{
                  background: deliveryMode === 'pickup' ? '#fff' : 'transparent',
                  color: deliveryMode === 'pickup' ? '#0a0a0a' : 'rgba(255,255,255,0.45)',
                }}
              >
                Самовывоз
              </div>
              {deliveryMode === 'delivery' && (
                <div
                  className="flex-1 py-2.5 text-sm font-semibold text-center rounded-xl"
                  style={{ background: '#fff', color: '#0a0a0a' }}
                >
                  Доставка
                </div>
              )}
            </div>
          </div>

          {deliveryMode === 'delivery' && (
            <div className="rounded-3xl p-4" style={cardStyle}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={labelStyle}>
                Адрес доставки
              </p>
              {deliveryAddress ? (
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="shrink-0 mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }} />
                  <p className="text-sm font-medium">
                    {deliveryAddress.formattedAddress || `${deliveryAddress.city}, ${deliveryAddress.street}, ${deliveryAddress.house}`}
                  </p>
                </div>
              ) : (
                <p className="text-sm" style={{ color: '#e53e3e' }}>
                  Адрес не выбран. Вернитесь на главную и укажите адрес доставки.
                </p>
              )}
            </div>
          )}

          <div className="rounded-3xl p-4" style={cardStyle}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={labelStyle}>
              Оплата
            </p>
            <div className="flex p-1 gap-1 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <button
                type="button"
                onClick={() => setPayMode('online')}
                className="flex-1 py-2.5 text-sm font-semibold text-center rounded-xl transition-all"
                style={{
                  background: payMode === 'online' ? '#fff' : 'transparent',
                  color: payMode === 'online' ? '#0a0a0a' : 'rgba(255,255,255,0.45)',
                }}
              >
                Онлайн
              </button>
              <button
                type="button"
                onClick={() => setPayMode('cash')}
                className="flex-1 py-2.5 text-sm font-semibold text-center rounded-xl transition-all"
                style={{
                  background: payMode === 'cash' ? '#fff' : 'transparent',
                  color: payMode === 'cash' ? '#0a0a0a' : 'rgba(255,255,255,0.45)',
                }}
              >
                При получении
              </button>
            </div>
          </div>

          {terminalGroups.length > 0 && (
            <div className="rounded-3xl p-4" style={cardStyle}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={labelStyle}>
                Точка самовывоза
              </p>
              <div className="space-y-2">
                {terminalGroups.map(group => (
                  <button
                    key={group.id}
                    onClick={() => setSelectedTerminalGroupId(group.id)}
                    className="w-full flex items-start gap-3 rounded-2xl px-4 py-3 transition-all text-left"
                    style={{
                      background: selectedTerminalGroupId === group.id ? '#fff' : 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: selectedTerminalGroupId === group.id ? '#0a0a0a' : '#fff',
                    }}
                  >
                    <MapPin
                      size={16}
                      className="shrink-0 mt-0.5"
                      style={{ color: selectedTerminalGroupId === group.id ? '#0a0a0a' : 'rgba(255,255,255,0.4)' }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{group.name}</p>
                      {group.address && (
                        <p
                          className="text-xs mt-0.5"
                          style={{
                            color: selectedTerminalGroupId === group.id
                              ? 'rgba(10,10,10,0.55)'
                              : 'rgba(255,255,255,0.4)',
                          }}
                        >
                          {group.address}
                        </p>
                      )}
                      {group.isAlive === false ? (
                        <p
                          className="text-[11px] mt-1 font-medium"
                          style={{
                            color: selectedTerminalGroupId === group.id
                              ? '#8a6a00'
                              : 'rgba(255,200,100,0.95)',
                          }}
                        >
                          Заказы временно не принимаются
                        </p>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-3xl p-4" style={cardStyle}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={labelStyle}>
              Контакт
            </p>
            <div
              className="flex items-center gap-3 rounded-2xl px-4 py-3"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <Phone size={16} style={{ color: 'rgba(255,255,255,0.4)' }} className="shrink-0" />
              <input
                type="tel"
                placeholder="+7 (999) 999-99-99"
                value={storedPhone ?? phone}
                onChange={e => setPhone(e.target.value)}
                readOnly={!!storedPhone}
                className="flex-1 bg-transparent text-sm font-medium"
                style={{ color: '#fff' }}
              />
            </div>
          </div>

          <div className="rounded-3xl p-4" style={cardStyle}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={labelStyle}>
              Комментарий
            </p>
            <textarea
              placeholder="Пожелания к заказу..."
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={2}
              className="w-full bg-transparent text-sm resize-none outline-none"
              style={{ color: '#fff' }}
            />
          </div>

          <div className="rounded-3xl p-4" style={cardStyle}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={labelStyle}>
              Состав заказа
            </p>
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.id} className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {item.name}
                    <span className="ml-1" style={{ color: 'rgba(255,255,255,0.35)' }}>×{item.quantity}</span>
                  </span>
                  <span className="text-sm font-semibold">
                    {item.price * item.quantity} ₽
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          className="fixed bottom-0 left-0 right-0 px-4 pt-4"
          style={{
            background: 'linear-gradient(to top, #0a0a0a 80%, transparent)',
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)',
          }}
        >
          <div className="max-w-md mx-auto">
            <AnimatePresence>
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-xs text-center mb-3 font-medium"
                  style={{ color: '#e53e3e' }}
                >
                  {error}
                </motion.p>
              )}
            </AnimatePresence>

            <div className="mb-3 px-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Сумма</span>
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{pricing.subtotal} ₽</span>
              </div>
              {pricing.discountSum > 0 && (
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm" style={{ color: '#e8c872' }}>Скидка {pricing.discountPercent}%</span>
                  <span className="text-sm" style={{ color: '#e8c872' }}>−{pricing.discountSum} ₽</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Итого</span>
                <span className="text-xl font-bold">{pricing.payable} ₽</span>
              </div>
            </div>

            {(deliveryMode === 'pickup' || deliveryMode === 'delivery') && (
              <p
                className="text-center text-xs mb-3 px-2 leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}
              >
                {deliveryMode === 'delivery'
                  ? 'Заказы на доставку временно не принимаются'
                  : 'Заказы на самовывоз временно не принимаются'}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={
                loading ||
                pointsLoading ||
                items.length === 0 ||
                deliveryMode === 'pickup' ||
                deliveryMode === 'delivery' ||
                !!scheduleClosed
              }
              className="w-full py-4 rounded-2xl font-bold text-base transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 tracking-[0.08em] uppercase"
              style={{
                background: '#fff',
                color: '#0a0a0a',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              {loading ? <Loader size={18} className="animate-spin" /> : null}
              {loading
                ? 'Отправляем...'
                : scheduleClosed
                  ? 'Кафе закрыто'
                  : payMode === 'online'
                    ? 'Перейти к оплате'
                    : 'Подтвердить заказ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
